import { useEffect, useState } from 'react';
import { Pencil, Plus, Trash2, Zap } from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../components/layout/Layout.tsx';
import ConfirmModal from '../components/ConfirmModal.tsx';
import {
  fetchRestaurant,
  fetchShortcuts,
  createShortcut,
  updateShortcut,
  deleteShortcut,
} from '../services/api.ts';

import { Shortcut } from '../types';

const AtalhosPage = () => {
  const [, setClienteId] = useState(null);
  const [items, setItems] = useState([] as Shortcut[]);
  const [loading, setLoading] = useState(true);
  const [newAtalho, setNewAtalho] = useState('');
  const [newMsg, setNewMsg] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editAtalho, setEditAtalho] = useState('');
  const [editMsg, setEditMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const rest = await fetchRestaurant();
      const id = (rest as any).id_cliente ?? rest.id;
      setClienteId(id);
      const data = await fetchShortcuts(id);
      setItems(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    if (!newAtalho.trim() || !newMsg.trim()) return;
    setSaving(true);
    try {
      const created = await createShortcut({ atalho: newAtalho.replace(/^\/+/, ''), mensagem: newMsg.replace(/^\/+/, '') });
      setItems([...items, created]);
      setNewAtalho('');
      setNewMsg('');
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const handleUpdate = async (id: number) => {
    if (!editAtalho.trim() && !editMsg.trim()) return;
    setSaving(true);
    try {
      await updateShortcut(id, { atalho: editAtalho.replace(/^\/+/, ''), mensagem: editMsg.replace(/^\/+/, '') });
      setItems(items.map(it => it.id === id ? { ...it, atalho: editAtalho.replace(/^\/+/, ''), mensagem: editMsg.replace(/^\/+/, '') } : it));
      setEditingId(null);
      setEditAtalho('');
      setEditMsg('');
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const handleDelete = (id: number) => {
    setDeleteTargetId(id);
  };

  const handleDeleteConfirm = async () => {
    if (deleteTargetId == null) return;
    setSaving(true);
    try {
      await deleteShortcut(deleteTargetId);
      setItems(items.filter(it => it.id !== deleteTargetId));
      setDeleteTargetId(null);
      toast.success('Atalho removido.');
    } catch (e) {
      console.error(e);
      toast.error('Erro ao remover atalho.');
    }
    setSaving(false);
  };

  const deleteTarget = deleteTargetId != null
    ? items.find(it => it.id === deleteTargetId) || null
    : null;

  return (
    <Layout>
      <ConfirmModal
        open={deleteTargetId != null}
        variant="destructive"
        title="Remover atalho?"
        description="Este atalho não poderá mais ser usado nas conversas."
        preview={deleteTarget ? (
          <div>
            <p className="font-mono text-sm font-semibold text-[#2f7d72]">/{deleteTarget.atalho}</p>
            <p className="mt-1 text-xs text-slate-600 break-words">{deleteTarget.mensagem}</p>
          </div>
        ) : null}
        confirmLabel={saving ? 'Removendo...' : 'Remover atalho'}
        saving={saving}
        onCancel={() => { if (!saving) setDeleteTargetId(null); }}
        onConfirm={handleDeleteConfirm}
      />
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="min-h-full bg-[#f6f7fb] px-3 py-6 text-slate-900 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1400px] space-y-5">
          <div className="mb-1 text-center">
            <h2 className="flex items-center justify-center gap-3 text-2xl font-black text-slate-900">
              <Zap size={28} className="text-[#2f7d72]" />
              Controle de mensagens rápidas
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-slate-600">
              Cadastre atalhos de mensagens para agilizar suas conversas.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-5 space-y-3">
            <div className="grid gap-3 md:grid-cols-[2fr_2fr]">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">/</span>
                <input
                  type="text"
                  placeholder="Atalho"
                  className="w-full border px-3 py-2 rounded pl-6 focus:outline-none focus:ring-1 focus:ring-teal-500"
                  value={newAtalho}
                  onChange={e => setNewAtalho(e.target.value.replace(/^\/+/, ''))}
                />
              </div>
              <input
                type="text"
                placeholder="Mensagem"
                className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleAdd}
                disabled={saving}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-4 text-sm font-semibold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Plus size={16} />
                Adicionar
              </button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="text-center text-gray-400 py-8">Nenhum atalho cadastrado.</div>
          ) : (
            <div className="space-y-3">
              {items.map(item => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow divide-y divide-gray-100 overflow-hidden"
                >
                  {editingId === item.id ? (
                    <div className="p-4 space-y-3">
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="relative">
                          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">/</span>
                          <input
                            type="text"
                            className="w-full border px-3 py-2 rounded pl-6 focus:outline-none focus:ring-1 focus:ring-teal-500"
                            value={editAtalho}
                            onChange={e => setEditAtalho(e.target.value.replace(/^\/+/, ''))}
                          />
                        </div>
                        <input
                          type="text"
                          className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                          value={editMsg}
                          onChange={e => setEditMsg(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          onClick={() => handleUpdate(item.id)}
                          disabled={saving}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-3 text-xs font-semibold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Salvar
                        </button>
                        <button
                          onClick={() => { setEditingId(null); setEditAtalho(''); setEditMsg(''); }}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
                      <div className="space-y-1">
                        <p className="font-mono text-teal-600 text-sm break-words">/{item.atalho}</p>
                        <p className="text-sm text-gray-700 break-words">{item.mensagem}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 justify-end">
                        <button
                          onClick={() => {
                            setEditingId(item.id);
                            setEditAtalho(item.atalho.startsWith('/') ? item.atalho.slice(1) : item.atalho);
                            setEditMsg(item.mensagem);
                          }}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 hover:border-slate-300"
                        >
                          <Pencil size={14} />
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 hover:border-rose-300"
                        >
                          <Trash2 size={14} />
                          Excluir
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AtalhosPage;
